import { useState, useCallback, useMemo } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Title,
  Table,
  Select,
  Group,
  Button,
  Badge,
  ActionIcon,
  Menu,
  Text,
  Paper,
  Stack,
  Skeleton,
  Modal,
  NumberInput,
  Switch,
  LoadingOverlay,
  TextInput,
  Textarea,
  Pagination,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconBan,
  IconPlayerPlay,
  IconTrash,
  IconUsers,
  IconX,
  IconDownload,
  IconFileSpreadsheet,
} from '@tabler/icons-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { licensesService } from '../../services/api/licenses';
import { customersService } from '../../services/api/customers';
import { productsService } from '../../services/api/products';
import { useCreateMutation, useUpdateMutation, useActionMutation, useFormModal, useTableSort, showValidationError } from '../../hooks';
import { SearchToolbar, FormModal, SortableHeader, DetailModal, DetailField } from '../../components/shared';
import { LICENSE_STATUS_COLORS } from '../../constants';
import type { LicenseWithDetails, LicenseStatus, CreateLicenseInput, UpdateLicenseInput } from '../../types';
import { useAuthStore } from '../../stores/auth';
import dayjs from 'dayjs';

interface LicenseActionFormData {
  reason: string;
}

export function LicensesPage() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const customerIdFilter = searchParams.get('customerId') || undefined;
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [expiringDays, setExpiringDays] = useState<number | null>(() => {
    const val = searchParams.get('expiring');
    return val ? parseInt(val, 10) || null : null;
  });
  const [trialFilter, setTrialFilter] = useState<string | null>(() => {
    return searchParams.get('trial') || null;
  });
  const [demoFilter, setDemoFilter] = useState<string | null>(() => {
    return searchParams.get('demo') || null;
  });
  const [page, setPage] = useState(1);
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const [viewModalOpened, { open: openViewModal, close: closeViewModal }] = useDisclosure(false);
  const [viewItem, setViewItem] = useState<LicenseWithDetails | null>(null);

  // Create form state (kept separate due to conditional modal content)
  const [createFormData, setCreateFormData] = useState<Partial<CreateLicenseInput>>({});

  // Edit modal with useFormModal
  const editModal = useFormModal<UpdateLicenseInput, LicenseWithDetails>({ initialData: {} });
  const revokeModal = useFormModal<LicenseActionFormData, LicenseWithDetails>({
    initialData: { reason: '' },
  });
  const suspendModal = useFormModal<LicenseActionFormData, LicenseWithDetails>({
    initialData: { reason: '' },
  });

  const { data: licensesData, isLoading: licensesLoading } = useQuery({
    queryKey: ['licenses', debouncedSearch, statusFilter, expiringDays, trialFilter, demoFilter, customerIdFilter],
    queryFn: () =>
      licensesService.search({
        query: debouncedSearch || undefined,
        customerId: customerIdFilter,
        status: (statusFilter as LicenseStatus) || undefined,
        isTrial: trialFilter === 'yes' ? true : trialFilter === 'no' ? false : undefined,
        isDemo: demoFilter === 'yes' ? true : demoFilter === 'no' ? false : undefined,
        expiringWithinDays: expiringDays || undefined,
        limit: 100,
      }),
  });

  const { data: customersData } = useQuery({
    queryKey: ['customers-list'],
    queryFn: () => customersService.list({ limit: 1000 }),
  });

  const { data: productsData } = useQuery({
    queryKey: ['products-list', 'active'],
    queryFn: () => productsService.list({ status: 'active' }),
  });

  const createMutation = useCreateMutation(
    (data: CreateLicenseInput) => licensesService.create(data),
    {
      queryKeys: ['licenses'],
      entityName: 'License',
      onSuccessCallback: () => {
        closeCreateModal();
        setCreateFormData({});
      },
    }
  );

  const updateMutation = useUpdateMutation(
    ({ id, data }: { id: string; data: UpdateLicenseInput }) => licensesService.update(id, data),
    {
      queryKeys: ['licenses'],
      entityName: 'License',
      onSuccessCallback: editModal.close,
    }
  );

  const suspendMutation = useActionMutation(
    ({ id, reason }: { id: string; reason: string }) => licensesService.suspend(id, reason),
    {
      queryKeys: ['licenses'],
      successMessage: 'License suspended',
      successColor: 'orange',
      onSuccessCallback: suspendModal.close,
    }
  );

  const reactivateMutation = useActionMutation(
    (id: string) => licensesService.reactivate(id),
    {
      queryKeys: ['licenses'],
      successMessage: 'License reactivated',
    }
  );

  const revokeMutation = useActionMutation(
    ({ id, reason }: { id: string; reason: string }) => licensesService.revoke(id, reason),
    {
      queryKeys: ['licenses'],
      successMessage: 'License revoked',
      successColor: 'red',
      onSuccessCallback: revokeModal.close,
    }
  );

  const handleCreateSubmit = () => {
    if (!createFormData.customerId || !createFormData.productId || !createFormData.eid) {
      showValidationError('Customer, Product, and EID are required');
      return;
    }
    const { trialDays, ...payload } = createFormData as CreateLicenseInput & { trialDays?: number };
    if (payload.isTrial && trialDays) {
      payload.termEnd = dayjs().add(trialDays, 'day').format('YYYY-MM-DD') + 'T12:00:00.000Z';
    }
    createMutation.mutate(payload as CreateLicenseInput);
  };

  const handleEditSubmit = () => {
    if (!editModal.selectedItem) return;
    const original = editModal.selectedItem;
    const form = editModal.formData;
    const diff: Partial<UpdateLicenseInput> = {};

    if (isAdmin && form.eid && form.eid !== original.eid) diff.eid = form.eid;
    if (form.customerId && form.customerId !== original.customerId) diff.customerId = form.customerId;
    if (form.productId && form.productId !== original.productId) diff.productId = form.productId;
    if (form.isTrial !== undefined && form.isTrial !== original.isTrial) diff.isTrial = form.isTrial;

    const formStart = form.termStart ? dayjs(form.termStart).format('YYYY-MM-DD') : '';
    const origStart = original.termStart ? dayjs(original.termStart).format('YYYY-MM-DD') : '';
    if (formStart !== origStart) diff.termStart = form.termStart;

    const formEnd = form.termEnd ? dayjs(form.termEnd).format('YYYY-MM-DD') : null;
    const origEnd = original.termEnd ? dayjs(original.termEnd).format('YYYY-MM-DD') : null;
    if (formEnd !== origEnd) diff.termEnd = form.termEnd;

    const formNotes = form.notes || '';
    const origNotes = original.notes || '';
    if (formNotes !== origNotes) diff.notes = form.notes;

    if (Object.keys(diff).length === 0) {
      editModal.close();
      return;
    }

    updateMutation.mutate({
      id: original.id,
      data: diff as UpdateLicenseInput,
    });
  };

  const openEdit = (license: LicenseWithDetails) => {
    editModal.openForEdit(license, {
      eid: license.eid,
      customerId: license.customerId,
      productId: license.productId,
      termStart: license.termStart,
      termEnd: license.termEnd,
      isTrial: license.isTrial,
      notes: license.notes || '',
    });
  };

  const handleRevoke = () => {
    if (!revokeModal.selectedItem || !revokeModal.formData.reason?.trim()) {
      showValidationError('Please provide a reason for revocation');
      return;
    }
    revokeMutation.mutate({
      id: revokeModal.selectedItem.id,
      reason: revokeModal.formData.reason,
    });
  };

  const handleSuspend = () => {
    if (!suspendModal.selectedItem || !suspendModal.formData.reason?.trim()) {
      showValidationError('Please provide a reason for suspension');
      return;
    }
    suspendMutation.mutate({
      id: suspendModal.selectedItem.id,
      reason: suspendModal.formData.reason,
    });
  };

  const resetPage = useCallback(() => setPage(1), []);
  const { sortColumn, sortDirection, handleSort, sortData } = useTableSort<LicenseWithDetails>(resetPage);

  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);

  const handleExportCurrentLicenses = useCallback(async () => {
    setExporting(true);
    try {
      const blob = await licensesService.exportCurrentLicenses();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Current Licenses Export - ${dayjs().format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showValidationError('Failed to export licenses');
    } finally {
      setExporting(false);
    }
  }, []);

  const handleExportActivationHistory = useCallback(async (license: LicenseWithDetails) => {
    try {
      const blob = await licensesService.exportActivationHistory(license.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `License Activation History - ${license.eid} - ${dayjs().format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showValidationError('Failed to export activation history');
    }
  }, []);

  const licenses = licensesData?.licenses || [];
  const customers = customersData?.customers || [];
  const products = productsData?.products || [];
  // A license stays on its side of the demo boundary, so the edit form only offers
  // products whose demo setting matches the license's current product
  const currentEditProduct = products.find(p => p.id === editModal.selectedItem?.productId);
  const editableProducts = currentEditProduct
    ? products.filter(p => p.isDemo === currentEditProduct.isDemo)
    : products;
  const filterCustomerName = customerIdFilter
    ? customers.find(c => c.id === customerIdFilter)?.name
    : undefined;

  const sortAccessors: Record<string, (item: LicenseWithDetails) => unknown> = useMemo(() => ({
    eid: (l) => l.eid,
    customer: (l) => l.customerName,
    product: (l) => l.productName,
    status: (l) => l.status,
    activations: (l) => l.currentActivations,
    termEnd: (l) => l.termEnd,
  }), []);

  const sortedLicenses = sortData(licenses, sortAccessors);

  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(sortedLicenses.length / PAGE_SIZE);
  const paginatedLicenses = sortedLicenses.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Title order={2}>Licenses</Title>
        <Group gap="sm">
          <Button
            variant="light"
            leftSection={<IconDownload size={16} />}
            onClick={handleExportCurrentLicenses}
            loading={exporting}
          >
            Export Current Licenses
          </Button>
          <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
            Create License
          </Button>
        </Group>
      </Group>

      <SearchToolbar
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        placeholder="Search by EID, customer..."
      >
        <Select
          placeholder="Status"
          clearable
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          data={[
            { value: 'draft', label: 'Draft' },
            { value: 'active', label: 'Active' },
            { value: 'suspended', label: 'Suspended' },
            { value: 'expired', label: 'Expired' },
            { value: 'revoked', label: 'Revoked' },
          ]}
          w={150}
        />
        <Select
          placeholder="Expiring"
          clearable
          value={expiringDays ? String(expiringDays) : null}
          onChange={(v) => {
            const days = v ? parseInt(v, 10) : null;
            setExpiringDays(days);
            setPage(1);
            const params: Record<string, string> = {};
            if (days) params.expiring = String(days);
            if (trialFilter) params.trial = trialFilter;
            if (demoFilter) params.demo = demoFilter;
            setSearchParams(params);
          }}
          data={[
            { value: '30', label: 'Next 30 days' },
            { value: '60', label: 'Next 60 days' },
            { value: '90', label: 'Next 90 days' },
          ]}
          w={160}
        />
        <Select
          placeholder="Trial"
          clearable
          value={trialFilter}
          onChange={(v) => {
            setTrialFilter(v);
            setPage(1);
            const params: Record<string, string> = {};
            if (expiringDays) params.expiring = String(expiringDays);
            if (v) params.trial = v;
            if (demoFilter) params.demo = demoFilter;
            setSearchParams(params);
          }}
          data={[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
          w={120}
        />
        <Select
          placeholder="Demo"
          clearable
          value={demoFilter}
          onChange={(v) => {
            setDemoFilter(v);
            setPage(1);
            const params: Record<string, string> = {};
            if (expiringDays) params.expiring = String(expiringDays);
            if (trialFilter) params.trial = trialFilter;
            if (v) params.demo = v;
            setSearchParams(params);
          }}
          data={[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
          w={120}
        />
      </SearchToolbar>

      {customerIdFilter && (
        <Paper withBorder p="xs" mb="sm" bg="blue.0">
          <Group gap="xs">
            <Text size="sm">
              Filtered by customer: <Text span fw={600}>{filterCustomerName || customerIdFilter}</Text>
            </Text>
            <ActionIcon size="xs" variant="subtle" onClick={() => navigate('/licenses')}>
              <IconX size={14} />
            </ActionIcon>
          </Group>
        </Paper>
      )}

      <Paper withBorder>
        {licensesLoading ? (
          <Stack p="md" gap="xs">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} height={50} />
            ))}
          </Stack>
        ) : licenses.length === 0 ? (
          <Text p="xl" ta="center" c="dimmed">
            No licenses found
          </Text>
        ) : (
          <>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <SortableHeader column="eid" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>EID</SortableHeader>
                <SortableHeader column="customer" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Customer</SortableHeader>
                <SortableHeader column="product" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Product</SortableHeader>
                <SortableHeader column="status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Status</SortableHeader>
                <SortableHeader column="activations" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Activations</SortableHeader>
                <SortableHeader column="termEnd" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Term End</SortableHeader>
                <Table.Th w={50}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginatedLicenses.map(license => (
                <Table.Tr key={license.id} onClick={() => { setViewItem(license); openViewModal(); }} style={{ cursor: 'pointer' }}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {license.eid}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{license.customerName}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Text size="sm">{license.productName}</Text>
                      {license.isTrial && (
                        <Badge color="gray" variant="light" size="xs">
                          Trial
                        </Badge>
                      )}
                      {license.productIsDemo && (
                        <Badge color="violet" variant="light" size="xs">
                          Demo
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={LICENSE_STATUS_COLORS[license.status]} variant="light">
                      {license.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {license.currentActivations} / {license.maxActivations}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {license.termEnd ? dayjs(license.termEnd).format('MMM D, YYYY') : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end" withArrow>
                      <Menu.Target>
                        <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                        <Menu.Item
                          leftSection={<IconEdit size={14} />}
                          onClick={() => openEdit(license)}
                        >
                          Edit
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconFileSpreadsheet size={14} />}
                          onClick={() => handleExportActivationHistory(license)}
                        >
                          Export Activation History
                        </Menu.Item>
                        {license.status === 'active' && (
                          <Menu.Item
                            leftSection={<IconBan size={14} />}
                            color="orange"
                            onClick={() =>
                              suspendModal.openForEdit(license, { reason: '' })
                            }
                          >
                            Suspend
                          </Menu.Item>
                        )}
                        {license.status === 'suspended' && (
                          <Menu.Item
                            leftSection={<IconPlayerPlay size={14} />}
                            color="green"
                            onClick={() => reactivateMutation.mutate(license.id)}
                          >
                            Reactivate
                          </Menu.Item>
                        )}
                        {license.status !== 'revoked' && (
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() =>
                              revokeModal.openForEdit(license, { reason: '' })
                            }
                          >
                            Revoke
                          </Menu.Item>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Group justify="space-between" px="md" py="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="sm" c="dimmed">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, sortedLicenses.length)}–{Math.min(page * PAGE_SIZE, sortedLicenses.length)} of {sortedLicenses.length}
            </Text>
            {totalPages > 1 && (
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            )}
          </Group>
          </>
        )}
      </Paper>

      {/* View License Modal */}
      {viewItem && (
        <DetailModal
          opened={viewModalOpened}
          onClose={closeViewModal}
          title="License Details"
          recordId={viewItem.id}
          actions={[
            {
              label: 'Edit',
              icon: <IconEdit size={16} />,
              onClick: () => { closeViewModal(); openEdit(viewItem); },
            },
            {
              label: 'Export Activation History',
              icon: <IconFileSpreadsheet size={16} />,
              onClick: () => handleExportActivationHistory(viewItem),
            },
            {
              label: 'Suspend',
              icon: <IconBan size={16} />,
              color: 'orange',
              onClick: () => { closeViewModal(); suspendModal.openForEdit(viewItem, { reason: '' }); },
              hidden: viewItem.status !== 'active',
            },
            {
              label: 'Reactivate',
              icon: <IconPlayerPlay size={16} />,
              color: 'green',
              onClick: () => { closeViewModal(); reactivateMutation.mutate(viewItem.id); },
              hidden: viewItem.status !== 'suspended',
            },
            {
              label: 'Revoke',
              icon: <IconTrash size={16} />,
              color: 'red',
              onClick: () => { closeViewModal(); revokeModal.openForEdit(viewItem, { reason: '' }); },
              hidden: viewItem.status === 'revoked',
            },
          ]}
        >
          <DetailField label="EID" value={viewItem.eid} />
          <DetailField label="Status" value={
            <Badge color={LICENSE_STATUS_COLORS[viewItem.status]} variant="light">{viewItem.status}</Badge>
          } />
          <DetailField label="Customer" value={viewItem.customerName} />
          <DetailField label="Customer Email" value={viewItem.customerEmail} />
          <DetailField label="Customer Org" value={viewItem.customerOrganization} />
          <DetailField label="Product" value={
            <Group gap="xs" wrap="nowrap">
              <Text size="sm">{viewItem.productName}</Text>
              {viewItem.productIsDemo && (
                <Badge color="violet" variant="light" size="xs">Demo</Badge>
              )}
            </Group>
          } />
          <DetailField label="Trial" value={
            <Badge color="gray" variant="light">{viewItem.isTrial ? 'Yes' : 'No'}</Badge>
          } />
          <DetailField label="Activations" value={`${viewItem.currentActivations} / ${viewItem.maxActivations}`} />
          <DetailField label="Term Start" value={viewItem.termStart ? dayjs(viewItem.termStart).format('MMM D, YYYY') : undefined} />
          <DetailField label="Term End" value={viewItem.termEnd ? dayjs(viewItem.termEnd).format('MMM D, YYYY') : undefined} />
          <DetailField label="Auto Transfer" value={
            <Badge color={viewItem.allowAutoTransfer ? 'green' : 'gray'} variant="light">{viewItem.allowAutoTransfer ? 'Enabled' : 'Disabled'}</Badge>
          } />
          <DetailField label="Transfer Count" value={String(viewItem.transferCount ?? 0)} />
          <DetailField label="Offline Grace" value={viewItem.offlineGracePeriodHours ? `${viewItem.offlineGracePeriodHours} hours` : undefined} />
          <DetailField label="Notes" value={viewItem.notes} multiline />
          {viewItem.revokedAt && (
            <DetailField label="Revoked At" value={dayjs(viewItem.revokedAt).format('MMM D, YYYY h:mm A')} />
          )}
          {viewItem.revocationReason && (
            <DetailField label="Revocation Reason" value={viewItem.revocationReason} multiline />
          )}
          <DetailField label="Created" value={dayjs(viewItem.createdAt).format('MMM D, YYYY h:mm A')} />
          <DetailField label="Updated" value={dayjs(viewItem.updatedAt).format('MMM D, YYYY h:mm A')} />
        </DetailModal>
      )}

      {/* Create License Modal */}
      <Modal opened={createModalOpened} onClose={closeCreateModal} title="Create License" size="md">
        <LoadingOverlay visible={createMutation.isPending} />
        {customers.length === 0 ? (
          <Stack gap="md" align="center" py="xl">
            <IconUsers size={48} color="gray" stroke={1.5} />
            <Text ta="center" c="dimmed">
              No customers found. A customer is required to create a license.
            </Text>
            <Button
              component={Link}
              to="/customers"
              leftSection={<IconUsers size={16} />}
              onClick={closeCreateModal}
            >
              Go to Customers
            </Button>
          </Stack>
        ) : products.length === 0 ? (
          <Stack gap="md" align="center" py="xl">
            <Text ta="center" c="dimmed">
              No products found. A product is required to create a license.
            </Text>
            <Button
              component={Link}
              to="/products"
              onClick={closeCreateModal}
            >
              Go to Products
            </Button>
          </Stack>
        ) : (
          <Stack gap="md">
            <TextInput
              label="EID"
              placeholder="SAP External ID"
              required
              value={createFormData.eid || ''}
              onChange={e => setCreateFormData({ ...createFormData, eid: e.target.value })}
            />
            <Select
              label="Customer"
              placeholder="Select customer"
              required
              searchable
              data={customers.map(c => ({ value: c.id, label: c.name }))}
              value={createFormData.customerId || null}
              onChange={value => setCreateFormData({ ...createFormData, customerId: value || '' })}
            />
            <Select
              label="Product"
              placeholder="Select product"
              required
              searchable
              data={products.map(p => ({ value: p.id, label: p.isDemo ? `${p.name} (Demo)` : p.name }))}
              value={createFormData.productId || null}
              onChange={value => setCreateFormData({ ...createFormData, productId: value || '' })}
            />
            <NumberInput
              label="Max Activations"
              value={1}
              disabled
            />
            <Switch
              label="Trial License"
              checked={createFormData.isTrial || false}
              onChange={e => {
                const checked = e.currentTarget.checked;
                if (checked) {
                  const days = createFormData.trialDays || 30;
                  const end = dayjs().add(days, 'day').format('YYYY-MM-DD');
                  setCreateFormData({ ...createFormData, isTrial: true, trialDays: days, termEnd: `${end}T12:00:00.000Z` });
                } else {
                  setCreateFormData({ ...createFormData, isTrial: false, termEnd: undefined });
                }
              }}
            />
            {createFormData.isTrial && (
              <NumberInput
                label="Trial Days"
                placeholder="30"
                min={1}
                allowDecimal={false}
                clampBehavior="strict"
                value={createFormData.trialDays || 30}
                onChange={value => {
                  const days = Number(value) || 30;
                  const end = dayjs().add(days, 'day').format('YYYY-MM-DD');
                  setCreateFormData({ ...createFormData, trialDays: days, termEnd: `${end}T12:00:00.000Z` });
                }}
                description={`Ends: ${dayjs().add(createFormData.trialDays || 30, 'day').format('MMM D, YYYY')}`}
              />
            )}
            <TextInput
              label="Term End"
              type="date"
              disabled={createFormData.isTrial || false}
              value={createFormData.termEnd ? dayjs(createFormData.termEnd).format('YYYY-MM-DD') : ''}
              onChange={e =>
                setCreateFormData({
                  ...createFormData,
                  termEnd: e.target.value ? `${e.target.value}T12:00:00.000Z` : undefined,
                })
              }
            />
            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={closeCreateModal}>
                Cancel
              </Button>
              <Button onClick={handleCreateSubmit}>Create</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Edit License Modal */}
      <FormModal
        opened={editModal.isOpen}
        onClose={editModal.close}
        title="Edit License"
        size="lg"
        isLoading={updateMutation.isPending}
        onSubmit={handleEditSubmit}
        submitLabel="Save Changes"
      >
        <TextInput
          label="EID"
          disabled={!isAdmin}
          description={isAdmin ? undefined : 'Admin access required to change after creation'}
          value={editModal.formData.eid || editModal.selectedItem?.eid || ''}
          onChange={e => editModal.setFormData({ ...editModal.formData, eid: e.target.value })}
        />
        <Select
          label="Customer"
          placeholder="Select customer"
          searchable
          data={customers.map(c => ({ value: c.id, label: c.name }))}
          value={editModal.formData.customerId || null}
          onChange={value => editModal.setFormData({ ...editModal.formData, customerId: value || '' })}
        />
        <Select
          label="Product"
          placeholder="Select product"
          searchable
          data={editableProducts.map(p => ({ value: p.id, label: p.isDemo ? `${p.name} (Demo)` : p.name }))}
          value={editModal.formData.productId || null}
          onChange={value => editModal.setFormData({ ...editModal.formData, productId: value || '' })}
        />
        <Text size="xs" c="dimmed" mt={-8}>
          A license can't be moved to or from a demo product.
        </Text>
        <NumberInput
          label="Max Activations"
          value={editModal.selectedItem?.maxActivations || 1}
          disabled
        />
        <TextInput
          label="Term Start"
          type="date"
          value={editModal.formData.termStart ? dayjs(editModal.formData.termStart).format('YYYY-MM-DD') : ''}
          onChange={e =>
            editModal.setFormData({
              ...editModal.formData,
              termStart: e.target.value ? `${e.target.value}T12:00:00.000Z` : undefined,
            })
          }
        />
        <Switch
          label="Trial License"
          checked={editModal.formData.isTrial || false}
          disabled
          description="Cannot change after creation"
        />
        {editModal.formData.isTrial && (
          <NumberInput
            label="Trial Days"
            min={1}
            allowDecimal={false}
            clampBehavior="strict"
            value={
              editModal.formData.termStart && editModal.formData.termEnd
                ? Math.round((new Date(editModal.formData.termEnd).getTime() - new Date(editModal.formData.termStart).getTime()) / (1000 * 60 * 60 * 24))
                : 30
            }
            disabled={!isAdmin}
            description={
              !isAdmin
                ? 'Admin access required to change'
                : editModal.formData.termEnd
                  ? `Ends: ${dayjs(editModal.formData.termEnd).format('MMM D, YYYY')}`
                  : undefined
            }
            onChange={value => {
              const days = Number(value) || 30;
              const termStart = editModal.formData.termStart || editModal.selectedItem?.termStart;
              const newEnd = dayjs(termStart).add(days, 'day').format('YYYY-MM-DD');
              editModal.setFormData({
                ...editModal.formData,
                termEnd: `${newEnd}T12:00:00.000Z`,
              });
            }}
          />
        )}
        <TextInput
          label="Term End"
          type="date"
          disabled={editModal.formData.isTrial || false}
          value={editModal.formData.termEnd ? dayjs(editModal.formData.termEnd).format('YYYY-MM-DD') : ''}
          onChange={e =>
            editModal.setFormData({
              ...editModal.formData,
              termEnd: e.target.value ? `${e.target.value}T12:00:00.000Z` : null,
            })
          }
        />
        <Textarea
          label="Notes"
          placeholder="Optional notes"
          minRows={3}
          value={editModal.formData.notes || ''}
          onChange={e => editModal.setFormData({ ...editModal.formData, notes: e.target.value })}
        />
        {editModal.selectedItem && editModal.selectedItem.currentActivations > 0 &&
          (editModal.formData.customerId !== editModal.selectedItem.customerId ||
           editModal.formData.productId !== editModal.selectedItem.productId) && (
          <Paper withBorder p="sm" bg="yellow.0" style={{ borderColor: 'var(--mantine-color-yellow-4)' }}>
            <Text size="sm" c="yellow.9">
              This license has {editModal.selectedItem.currentActivations} active activation(s).
              Changing the customer or product may affect existing activations.
            </Text>
          </Paper>
        )}
      </FormModal>

      {/* Revoke License Modal */}
      <FormModal
        opened={revokeModal.isOpen}
        onClose={revokeModal.close}
        title="Revoke License"
        isLoading={revokeMutation.isPending}
        onSubmit={handleRevoke}
        submitLabel="Revoke"
        submitColor="red"
      >
        <Text size="sm">
          Are you sure you want to revoke this license? This action is permanent and cannot be undone.
        </Text>
        {revokeModal.selectedItem && (
          <Paper withBorder p="sm" bg="gray.0">
            <Text size="sm" fw={500}>{revokeModal.selectedItem.eid}</Text>
          </Paper>
        )}
        <Textarea
          label="Reason"
          placeholder="Enter reason for revocation"
          required
          value={revokeModal.formData.reason || ''}
          onChange={e => revokeModal.setFormData({ ...revokeModal.formData, reason: e.target.value })}
          minRows={3}
        />
      </FormModal>

      {/* Suspend License Modal */}
      <FormModal
        opened={suspendModal.isOpen}
        onClose={suspendModal.close}
        title="Suspend License"
        isLoading={suspendMutation.isPending}
        onSubmit={handleSuspend}
        submitLabel="Suspend"
        submitColor="orange"
      >
        <Text size="sm">
          Are you sure you want to suspend this license? The license can be reactivated later.
        </Text>
        {suspendModal.selectedItem && (
          <Paper withBorder p="sm" bg="gray.0">
            <Text size="sm" fw={500}>{suspendModal.selectedItem.eid}</Text>
          </Paper>
        )}
        <Textarea
          label="Reason"
          placeholder="Enter reason for suspension"
          required
          value={suspendModal.formData.reason || ''}
          onChange={e => suspendModal.setFormData({ ...suspendModal.formData, reason: e.target.value })}
          minRows={3}
        />
      </FormModal>
    </Container>
  );
}
